import React, { Component } from 'react';
import FlapBoard from '@components/FlapBoard';

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

const US_STATES = {
  'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA',
  'Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Georgia':'GA',
  'Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA',
  'Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD',
  'Massachusetts':'MA','Michigan':'MI','Minnesota':'MN','Mississippi':'MS',
  'Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV',
  'New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY',
  'North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK',
  'Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC',
  'South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT',
  'Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV',
  'Wisconsin':'WI','Wyoming':'WY','District of Columbia':'DC',
};

const WMO_MAP = [
  { codes: [0, 1],         text: 'CLEAR ', icon: 'fas fa-sun',       color: 'yellow'   },
  { codes: [2],            text: 'PARTLY', icon: 'fas fa-cloud',     color: 'skyblue'  },
  { codes: [3],            text: 'CLOUDY', icon: 'fas fa-cloud',     color: 'lavender' },
  { codes: [45, 48],       text: 'FOGGY ', icon: 'fas fa-cloud',     color: 'white'    },
  { codes: [51, 53, 55],   text: 'DRIZZY', icon: 'fas fa-tint',      color: 'skyblue'  },
  { codes: [61, 63, 65],   text: 'RAINY ', icon: 'fas fa-umbrella',  color: 'blue'     },
  { codes: [71, 73, 75],   text: 'SNOWY ', icon: 'fas fa-snowflake', color: 'white'    },
  { codes: [80, 81, 82],   text: 'SHWRS ', icon: 'fas fa-umbrella',  color: 'blue'     },
  { codes: [95, 96, 99],   text: 'STORMY', icon: 'fas fa-bolt',      color: 'violet'   },
];

function getCondition(code) {
  const match = WMO_MAP.find(entry => entry.codes.includes(code));
  return match
    ? { text: match.text, icon: match.icon, color: match.color }
    : { text: '------', icon: 'fas fa-question', color: 'black' };
}

function formatTempDesktop(temp) {
  // 4 chars right-aligned: " 72F", "100F", " -5F"
  return (String(temp) + 'F').padStart(4, ' ').slice(-4);
}

function formatTempMobile(temp) {
  // 3 chars: "72F", "100" (drops F if >= 100), " 5F" (padded if single digit)
  const s = String(temp) + 'F';
  return s.length <= 3 ? s.padEnd(3, ' ') : String(temp).slice(0, 3);
}

function makeRows(cols, weather, themeColors) {
  const now = new Date();
  const hours24 = now.getHours();
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  const hh = String(hours24 % 12 || 12).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');
  const mon = MONTHS[now.getMonth()];
  const dd = String(now.getDate()).padStart(2, '0');
  const yr = now.getFullYear();

  const ampmColor = ampm === 'AM' ? 'yellow' : 'orange';
  // g(i) maps group index → theme color (or null to use default)
  const g = (i) => themeColors ? themeColors[i % themeColors.length] : null;

  const timeBase = `${hh}:${mm}:${ss} ${ampm}`;
  const timeStr = timeBase.padEnd(cols, ' ');
  const timeColors = [
    g(0) || 'violet', g(0) || 'violet', 'black',
    g(1) || 'blue',   g(1) || 'blue',   'black',
    g(2) || 'green',  g(2) || 'green',  'black',
    g(3) || ampmColor, g(3) || ampmColor,
    ...Array(Math.max(0, cols - 11)).fill('black'),
  ];

  const dateBase = `${mon} ${dd} ${yr}`;
  const dateStr = dateBase.padEnd(cols, ' ');
  const dateColors = [
    g(4) || 'orange', g(4) || 'orange', g(4) || 'orange', 'black',
    g(5) || 'red',    g(5) || 'red',    'black',
    g(0) || 'pink',   g(0) || 'pink',   g(0) || 'pink',   g(0) || 'pink',
    ...Array(Math.max(0, cols - 11)).fill('black'),
  ];

  const blankRow = { text: ' '.repeat(cols), colors: Array(cols).fill('black'), icons: Array(cols).fill('') };

  const rows = [
    { text: timeStr, colors: timeColors, icons: Array(cols).fill('') },
    { text: dateStr, colors: dateColors, icons: Array(cols).fill('') },
    ...(cols < 20 ? [blankRow, blankRow] : []),
  ];

  if (!weather) return rows;

  const { temp, conditionText, conditionIcon, conditionColor, city, state } = weather;

  if (cols >= 20) {
    // Desktop: weave into trailing tiles
    // Time row: icon @ 12, temp @ 14-17, city @ 19-26
    const timeArr = timeStr.split('');
    const tempStr4 = formatTempDesktop(temp);
    for (let i = 0; i < 4; i++) timeArr[14 + i] = tempStr4[i];
    for (let i = 0; i < 8 && (19 + i) < cols; i++) timeArr[19 + i] = city[i] || ' ';
    const timeIcons = Array(cols).fill('');
    timeIcons[12] = conditionIcon;
    const weatherTimeColors = [...timeColors];
    weatherTimeColors[12] = conditionColor;
    for (let i = 0; i < 4; i++) {
      if (tempStr4[i] !== ' ') weatherTimeColors[14 + i] = conditionColor;
    }
    for (let i = 0; i < 8 && (19 + i) < cols; i++) {
      if ((city[i] || ' ') !== ' ') weatherTimeColors[19 + i] = 'white';
    }
    rows[0] = { text: timeArr.join(''), colors: weatherTimeColors, icons: timeIcons };

    // Date row: conditionText @ 12-17, state @ 19-20
    const dateArr = dateStr.split('');
    const cond6 = conditionText.slice(0, 6);
    for (let i = 0; i < 6; i++) dateArr[12 + i] = cond6[i];
    for (let i = 0; i < 2 && (19 + i) < cols; i++) dateArr[19 + i] = state[i] || ' ';
    const weatherDateColors = [...dateColors];
    for (let i = 0; i < 6; i++) {
      if (cond6[i] !== ' ') weatherDateColors[12 + i] = conditionColor;
    }
    for (let i = 0; i < 2 && (19 + i) < cols; i++) {
      if ((state[i] || ' ') !== ' ') weatherDateColors[19 + i] = 'white';
    }
    rows[1] = { text: dateArr.join(''), colors: weatherDateColors, icons: Array(cols).fill('') };
  } else {
    // Mobile: append 3rd weather row
    const mobArr = Array(cols).fill(' ');
    const tempStr3 = formatTempMobile(temp);
    for (let i = 0; i < 3; i++) mobArr[2 + i] = tempStr3[i];
    mobArr[5] = ' ';
    const cond6 = conditionText.slice(0, 6);
    for (let i = 0; i < 6 && (6 + i) < cols; i++) mobArr[6 + i] = cond6[i];
    const mobIcons = Array(cols).fill('');
    mobIcons[0] = conditionIcon;
    const mobColors = Array(cols).fill('black');
    mobColors[0] = conditionColor;
    for (let i = 0; i < 3; i++) {
      if (tempStr3[i] !== ' ') mobColors[2 + i] = conditionColor;
    }
    for (let i = 0; i < 6 && (6 + i) < cols; i++) {
      if (cond6[i] !== ' ') mobColors[6 + i] = conditionColor;
    }
    rows[2] = {
      text: mobArr.join(''),
      colors: mobColors,
      icons: mobIcons,
    };

    // Mobile row 3: city (cols 0-7) + space (col 8) + state (cols 9-10)
    const locArr = Array(cols).fill(' ');
    for (let i = 0; i < 8 && i < cols; i++) locArr[i] = city[i] || ' ';
    for (let i = 0; i < 2 && (9 + i) < cols; i++) locArr[9 + i] = state[i] || ' ';
    const locColors = Array(cols).fill('black');
    for (let i = 0; i < 8 && i < cols; i++) {
      if (locArr[i] !== ' ') locColors[i] = 'white';
    }
    for (let i = 0; i < 2 && (9 + i) < cols; i++) {
      if (locArr[9 + i] !== ' ') locColors[9 + i] = 'white';
    }
    rows[3] = {
      text: locArr.join(''),
      colors: locColors,
      icons: Array(cols).fill(''),
    };
  }

  return rows;
}

class ClockBoard extends Component {
  constructor(props) {
    super(props);
    this._unmounted = false;
    this._clockInterval = null;
    this._weatherInterval = null;
    this.fetchWeather = this.fetchWeather.bind(this);
    this.handleThemeColors = this.handleThemeColors.bind(this);
    this.state = {
      rows: makeRows(props.cols || 12, null, null),
      weather: null,
      themeColors: null,
    };
  }

  componentDidMount() {
    const cols = this.props.cols || 12;
    this._clockInterval = setInterval(() => {
      this.setState(prevState => ({
        rows: makeRows(cols, prevState.weather, prevState.themeColors),
      }));
    }, 1000);
    this.fetchWeather();
    this._weatherInterval = setInterval(this.fetchWeather, 15 * 60 * 1000);
    document.addEventListener('flapboard-theme-applied', this.handleThemeColors);
  }

  componentWillUnmount() {
    this._unmounted = true;
    clearInterval(this._clockInterval);
    clearInterval(this._weatherInterval);
    document.removeEventListener('flapboard-theme-applied', this.handleThemeColors);
  }

  handleThemeColors(e) {
    const themeColors = e.detail.colors;
    const cols = this.props.cols || 12;
    this.setState(prevState => ({
      themeColors,
      rows: makeRows(cols, prevState.weather, themeColors),
    }));
  }

  async fetchWeather() {
    const cols = this.props.cols || 12;
    const fetchWithTimeout = (url) =>
      Promise.race([
        fetch(url),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
      ]);

    try {
      let latitude, longitude, rawCity, region, country;

      try {
        const r = await fetchWithTimeout('https://ipinfo.io/json');
        if (this._unmounted) return;
        if (r.ok) {
          const g = await r.json();
          if (g.city && g.loc) {
            const [lat, lon] = g.loc.split(',').map(Number);
            if (!isNaN(lat) && !isNaN(lon)) {
              latitude = lat; longitude = lon;
              rawCity = g.city; region = g.region; country = g.country;
            }
          }
        }
      } catch (_) {}

      if (!latitude || !rawCity) {
        try {
          const r = await fetchWithTimeout('https://freeipapi.com/api/json');
          if (this._unmounted) return;
          if (r.ok) {
            const g = await r.json();
            if (g.latitude && g.cityName) {
              latitude = g.latitude; longitude = g.longitude;
              rawCity = g.cityName; region = g.regionName; country = g.countryCode;
            }
          }
        } catch (_) {}
      }

      if (!latitude || !rawCity) {
        try {
          const r = await fetchWithTimeout('https://api.db-ip.com/v2/free/self');
          if (this._unmounted) return;
          if (r.ok) {
            const g = await r.json();
            if (g.latitude && g.city) {
              latitude = g.latitude; longitude = g.longitude;
              rawCity = g.city; region = g.stateProv; country = g.countryCode;
            }
          }
        } catch (_) {}
      }

      if (!latitude || isNaN(latitude) || !rawCity) return;
      if (this._unmounted) return;

      const stateAbbr = country === 'US'
        ? (US_STATES[region] || (region || '').slice(0, 2).toUpperCase())
        : (country || '??').slice(0, 2).toUpperCase();

      const weatherRes = await fetchWithTimeout(
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&temperature_unit=fahrenheit`
      );
      if (this._unmounted) return;
      const data = await weatherRes.json();
      const { temperature_2m, weather_code } = data.current;
      const { text: conditionText, icon: conditionIcon, color: conditionColor } = getCondition(weather_code);

      const weather = {
        temp: Math.round(temperature_2m),
        conditionText,
        conditionIcon,
        conditionColor,
        city: rawCity.toUpperCase().padEnd(8, ' ').slice(0, 8),
        state: stateAbbr,
      };

      this.setState(prevState => ({
        weather,
        rows: makeRows(cols, weather, prevState.themeColors),
      }));
    } catch (_) {
      // Silent fail — weather state stays null, tiles stay black
    }
  }

  render() {
    return (
      <FlapBoard
        rows={this.state.rows}
        autoPlay={true}
        loop={true}
      />
    );
  }
}

export default ClockBoard;



// WEBPACK FOOTER //
// ./src/components/ClockBoard/ClockBoard.js